/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import * as React from 'react';
import { defineMessages } from 'react-intl';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useProgressTracker } from 'src/core/hooks/useProgressTracker';
import { logButtonClick } from 'src/core/services/telemetry/eventsNamesTemplate';
import { FileType } from 'src/core/types/FileType';
import { AddToastParameters } from 'src/core/utils/decorators/addToast';
import { getPortalFromUrl } from 'src/core/utils/routingUtils';
import { QnaMakerSynonymsUiStore } from 'src/qnaMaker/appShell/qnaMakerEditPage/QnaMakerSynonymsUiStore';
import { QnaMakerSynonymSwagger } from 'src/qnaMaker/core/services/qnas/QnaMakerAssetsSwaggers';

const messages = defineMessages({
    saveChangesRunning: {
        id: 'qna.useSynonymsActions.saveChangesRunning',
        defaultMessage: 'Saving changes...'
    },
    saveChangesCompleted: {
        id: 'qna.useSynonymsActions.saveChangesCompleted',
        defaultMessage: 'Changes are saved'
    },
    saveChangesFailed: {
        id: 'qna.useSynonymsActions.saveChangesFailed',
        defaultMessage: 'Failed to save changes'
    },
    importSynonymsRunning: {
        id: 'qna.useSynonymsActions.importSynonymsRunning',
        defaultMessage: 'Importing synonyms...'
    },
    importSynonymsCompleted: {
        id: 'qna.useSynonymsActions.importSynonymsCompleted',
        defaultMessage: 'Synonyms imported'
    },
    importSynonymsFailed: {
        id: 'qna.useSynonymsActions.importSynonymsFailed',
        defaultMessage: 'Failed to import synonyms'
    },
    exportSynonymsRunning: {
        id: 'qna.useSynonymsActions.exportSynonymsRunning',
        defaultMessage: 'Exporting synonyms...'
    },
    exportSynonymsCompleted: {
        id: 'qna.useSynonymsActions.exportSynonymsCompleted',
        defaultMessage: 'Synonyms exported'
    },
    exportSynonymsFailed: {
        id: 'qna.useSynonymsActions.exportSynonymsFailed',
        defaultMessage: 'Failed to export synonyms'
    }
});

/**
 * A hook for facilitating using synonyms handlers.
 */
export const useSynonymsActions = (localStore: QnaMakerSynonymsUiStore) => {
    const { dispatcher, rootStore } = useGlobalState();
    const { localize } = useLocalization();
    const { serviceName } = getPortalFromUrl();

    const savingTracker = useProgressTracker({
        onComplete: () => {
            localStore.setSavingState('saved');
        }
    });

    const defaultTracker = useProgressTracker({
        onComplete: () => {
            localStore.setSavingState('saved');
        }
    });

    const deleteSynonym = React.useCallback(
        (synonymsIds: number[]) => {
            localStore.setDeleteModalSettings({ hidden: true, operation: null });

            localStore.deleteDisplayedSynonyms(synonymsIds);

            localStore.setSavingState('dirty');
        },
        [localStore]
    );

    const addSynonym = React.useCallback(
        (synonymData: Partial<QnaMakerSynonymSwagger>) => {
            localStore.addDisplayedSynonyms(<QnaMakerSynonymSwagger>{ ...synonymData });
            localStore.setIsAddSynonymOn(false);
            localStore.setSavingState('dirty');
        },
        [localStore]
    );

    const editSynonym = React.useCallback(
        (synonymId: number, newData: Partial<QnaMakerSynonymSwagger>) => {
            localStore.editDisplayedSynonym(synonymId, newData);
            localStore.setSavingState('dirty');
        },
        [localStore]
    );

    const saveChanges = React.useCallback(
        (fetchUpdatedObjects: boolean = true, toastData: AddToastParameters['toastData'] = undefined) => {
            localStore.setSavingState('inProgress');
            rootStore.currentQnaMakerProjectStore.resetSynonymsLoadedState();
            dispatcher.dispatch('saveQnaMakerAssets', {
                fetchUpdatedObjects,
                assetWithOperations: localStore.SynonymsJson,
                tracker: savingTracker,
                assetsType: 'synonyms',
                toastData: toastData || {
                    running: { title: localize(messages.saveChangesRunning) },
                    completed: { title: localize(messages.saveChangesCompleted) },
                    failed: { title: localize(messages.saveChangesFailed) }
                }
            });
        },
        [dispatcher.dispatch]
    );

    const importSynonyms = React.useCallback(
        ({ file, fileType }: { file: File; fileType: Extract<FileType, 'xlsx' | 'tsv'> }) => {
            dispatcher.dispatch('importQnaMakerSynonyms', {
                file,
                fileType,
                toastData: {
                    running: { title: localize(messages.importSynonymsRunning) },
                    completed: { title: localize(messages.importSynonymsCompleted) },
                    failed: { title: localize(messages.importSynonymsFailed) }
                },
                assetsType: 'synonyms'
            });
        },
        [dispatcher.dispatch]
    );

    const exportSynonyms = React.useCallback(
        (fileType: Extract<FileType, 'xlsx' | 'tsv'>) => {
            dispatcher.dispatch('exportQnaMakerAssets', {
                fileType,
                toastData: {
                    running: { title: localize(messages.exportSynonymsRunning) },
                    completed: { title: localize(messages.exportSynonymsCompleted) },
                    failed: { title: localize(messages.exportSynonymsFailed) }
                },
                assetsType: 'synonyms'
            });
            logButtonClick(
                serviceName,
                'EditKb',
                fileType === 'xlsx' ? 'ExportExcel' : 'ExportTsv',
                'Synonyms',
                rootStore?.currentQnaMakerProjectStore?.id
            );
        },
        [dispatcher.dispatch]
    );

    return {
        addSynonym,
        exportSynonyms,
        importSynonyms,
        editSynonym,
        saveChanges,
        deleteSynonym,
        savingTracker,
        defaultTracker
    };
};
